import { useState } from "react";
import { Link } from "react-router-dom";
import { galleryList } from "@/mocks/gallery";
import { newsList } from "@/mocks/news";
import { worksList } from "@/mocks/works";
import { formatChineseDate } from "@/utils/format";

const tabs = [
  { key: "news", label: "新闻" },
  { key: "works", label: "影视" },
  { key: "gallery", label: "写真" },
] as const;

type TabKey = (typeof tabs)[number]["key"];

const searchPool = {
  news: newsList.map((item) => ({
    id: item.id,
    title: item.title,
    meta: `${item.category} · ${formatChineseDate(item.publishedAt)}`,
    href: `/news/${item.id}`,
  })),
  works: worksList.map((item) => ({
    id: item.id,
    title: item.title,
    meta: `${item.year} · ${item.type} · ${item.role}`,
    href: `/works/${item.id}`,
  })),
  gallery: galleryList.map((item) => ({
    id: item.id,
    title: item.title,
    meta: `${item.category} · ${item.date}`,
    href: "/gallery",
  })),
};

export default function HomeSearch() {
  const [tab, setTab] = useState<TabKey>("news");
  const [keyword, setKeyword] = useState("");
  const [submitted, setSubmitted] = useState("");

  const results = submitted
    ? searchPool[tab].filter((item) => `${item.title}${item.meta}`.includes(submitted)).slice(0, 4)
    : [];

  return (
    <section className="w-full border-y border-background-200 bg-background-100 px-4 py-14 md:px-8 md:py-16 lg:px-12">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-xl">
          <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.28em] text-primary-600">
            <span className="h-[1px] w-6 bg-primary-400"></span>
            Quick Find
          </div>
          <h2 className="mt-3 font-heading text-2xl font-semibold text-foreground-950 md:text-[28px]">
            在站内快速定位你想看的内容
          </h2>
          <p className="mt-2 text-sm leading-7 text-foreground-600">
            新闻、影视作品与写真图集三大分类同时检索，输入关键词即可预览结果。
          </p>
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitted(keyword.trim());
          }}
          className="w-full max-w-xl"
        >
          <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-2 py-1.5 focus-within:border-primary-400">
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
              {tabs.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key)}
                  aria-pressed={tab === item.key}
                  className={`cursor-pointer rounded-full px-3 py-1 text-xs transition-colors ${
                    tab === item.key
                      ? "bg-background-50 text-primary-700"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="例如：南方来信、公益、写真"
              aria-label="站内快速搜索关键词"
              className="w-full bg-transparent px-1 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors hover:bg-primary-600"
              aria-label="开始搜索"
            >
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </button>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 pl-2 text-[11px] text-foreground-500">
            {submitted ? (
              <span>
                关键词「{submitted}」在{tab === "news" ? "新闻" : tab === "works" ? "影视" : "写真"}分类中找到{" "}
                {results.length} 条结果（前端演示）
              </span>
            ) : (
              <span>提示：搜索结果基于站内示范数据生成</span>
            )}
          </div>
        </form>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
        {results.map((item) => (
          <Link
            key={item.id}
            to={item.href}
            className="group flex items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-50 px-4 py-3"
          >
            <div className="min-w-0">
              <p className="truncate text-sm text-foreground-900 transition-colors group-hover:text-primary-700">
                {item.title}
              </p>
              <p className="mt-0.5 text-[11px] text-foreground-500">{item.meta}</p>
            </div>
            <i className="ri-arrow-right-up-line text-foreground-400 transition-colors group-hover:text-primary-600" aria-hidden="true"></i>
          </Link>
        ))}
        {submitted && results.length === 0 && (
          <p className="col-span-full rounded-lg bg-background-50 px-4 py-6 text-center text-xs text-foreground-500">
            没有找到与「{submitted}」相关的{tab === "news" ? "新闻" : tab === "works" ? "影视" : "写真"}，试试「南方来信」「公益」或前往搜索页查看更多结果。
          </p>
        )}
      </div>
    </section>
  );
}